import { Dialog, Slide, Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useState, useRef } from "react";
import Spinner from "../../../components/spinner";
import Loader from "../../../components/loader";
import Editor from "../../../components/elements/Editor";
import useCreate from "../../../hooks/useCreate";
import useUploadMedia from "../../../hooks/file/useUploadMedia";
import toast from "react-hot-toast";
import DeleteIcon from "@mui/icons-material/Delete";


const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function AddTestimonial({ open, handleClose }) {

    // Form State
    const [formData, setFormData] = useState({
        name: "",
        designation_company: "",
        content: "",
        image: "",
        active: "1"
    });

    // Image Upload State
    const fileInputRef = useRef(null);
    const [imagePreview, setImagePreview] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleChangeEditor = (value) => {
        setFormData({ ...formData, content: value });
    };

    // Create Hook
    const { setDataToServer, isLoading: isSubmitting } = useCreate({
        url: 'add-testimonial',
        refreshUrl: 'get-testimonial-list',
        onSuccess: () => {
            toast.success("Testimonial Created Successfully");
            resetForm();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || err?.message || "Error creating testimonial");
            console.error(err);
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.image) {
            toast.error("Please upload a client image");
            return;
        }
        setDataToServer.mutate(formData);
    };

    const closeErrorMessage = () => {
        // Handled by hook reset usually or manual state if we extracted it params
    };

    // Image Upload Hook
    const { upload, isLoading: isUploading } = useUploadMedia({
        url: "upload-document",
        onSuccess: (res) => {
            const imagePath = res?.image_path;
            if (imagePath) {
                setFormData(prev => ({ ...prev, image: imagePath }));
                setImagePreview(imagePath);
                toast.success("Image Uploaded Successfully");
            }
        },
        onError: (err) => {
            toast.error(`Image Upload Failed: ${err?.message}`);
        }
    });

    const handleUploadFile = (e) => {
        const file = e.target?.files[0];
        if (!file) return;

        const data = {
            file: file,
            type: 'testimonials'
        };
        upload.mutate(data);
        e.target.value = null; // Reset input
    };

    const handleOpenFileUploadDialog = () => {
        fileInputRef.current.click();
    };

    const handleDeleteImage = () => {
        setFormData(prev => ({ ...prev, image: "" }));
        setImagePreview(null);
    };

    // Reset form when closed or successful
    const resetForm = () => {
        setFormData({
            name: "",
            designation_company: "",
            content: "",
            image: "",
            active: "1"
        });
        setImagePreview(null);
        handleClose();
    };

    // If success, we can show alert then close or rely on user to close
    // In AddBlogs, it shows Alert and keeps open until user closes or navigates back.
    // We will follow the pattern: Show Success Alert in form, and redirect/close on back or timeout?
    // AddBlogs just shows alert.

    return (
        <Dialog
            fullScreen
            fullWidth={true}
            open={open}
            onClose={resetForm}
            TransitionComponent={Transition}
            disableEnforceFocus={true}
            disableAutoFocus={true}
        >
            <div className="container_xxl px-5">
                <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-40 bg-white">
                    <div>
                        <IconButton onClick={resetForm}>
                            <ArrowBackIcon />
                        </IconButton>
                    </div>
                    <div>
                        <h1 className="heading">Add Testimonial</h1>
                    </div>
                </div>
                <div className="form-body py-8">
                    <form
                        className=""
                        autoComplete="off"
                        onSubmit={handleSubmit}
                    >
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="label">
                                    Name <small className="text-red-700">*</small>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Client Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    name="name"
                                    className="input mt-4"
                                />
                            </div>

                            <div>
                                <label className="label">
                                    Designation / Company <small className="text-red-700">*</small>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Designation or Company"
                                    value={formData.designation_company}
                                    onChange={handleChange}
                                    name="designation_company"
                                    className="input mt-4"
                                />
                            </div>

                            {/* Image Section */}
                            <div className="">
                                <label className="label">
                                    Client Image <small className="text-red-700">*</small>
                                </label>

                                {isUploading ? (
                                    <Spinner />
                                ) : (
                                    <>
                                        {imagePreview && (
                                            <div className="my-4">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="object-contain w-[200px] h-auto border mb-2"
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={handleOpenFileUploadDialog}
                                                        disabled={isUploading}
                                                    >
                                                        Change Image
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={handleDeleteImage}
                                                        startIcon={<DeleteIcon />}
                                                    >
                                                        Remove Image
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="mt-3">
                                    {!imagePreview && (
                                        <Button
                                            type="button"
                                            variant="contained"
                                            color="success"
                                            className="primaryBtn-contained"
                                            onClick={handleOpenFileUploadDialog}
                                            disabled={isUploading}
                                        >
                                            Add Image
                                        </Button>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        onChange={handleUploadFile}
                                        accept="image/*"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">
                                    Content <small className="text-red-700">*</small>
                                </label>
                                <div className="mt-5">
                                    <Editor
                                        value={formData.content}
                                        onChange={handleChangeEditor}
                                    />
                                </div>
                            </div>

                        </div>

                        <div className="text-center my-6">
                            <Button
                                type="submit"
                                variant="contained"
                                className="primaryBtn-contained"
                                disabled={isSubmitting}
                            >
                                <div className="flex items-center gap-2">
                                    {isSubmitting && <Loader className="h-4 w-4" />}
                                    Save Testimonial
                                </div>
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Dialog>
    );
}

export default AddTestimonial;
